import { useAFormHandler } from 'analytics/hooks'
import { useFormik } from 'formik'
import { endpoints } from 'modules/endpoints'
import { RequestError } from 'modules/errors'
import { apiClient } from 'modules/utils/httpClient'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { ResponsiveModal } from 'views/components/ResponsiveModal/ResponsiveModal'
import { ElevatorCommonFields, ElevatorFormFields } from './ElevatorCommonFields'
import { Elevator } from 'modules/domain/warehouses/types'
import { removePropertyNamePrefix } from 'modules/utils/helpers'

type Props = {
  onClose: () => void
  onSuccess: () => void
  elevator: Elevator
}

export const EditElevatorModal: FC<Props> = ({ onClose, onSuccess, elevator }) => {
  const { t } = useTranslation('warehouses')
  const { formProgress, formHandler } = useAFormHandler()
  const costs = elevator.elevator_costs[0]
  const formik = useFormik<ElevatorFormFields>({
    initialValues: {
      title: elevator.title,
      address: elevator.address.id.toString(),
      company: elevator.company.id.toString(),
      grain_elevator_railway_station: elevator.railway_station?.id.toString() ?? null,
      capacity: elevator.capacity ?? null,
      provided_services: elevator.provided_services ?? '',
      acceptance_costs: costs?.acceptance_costs ?? null,
      storage_costs: costs?.storage_costs ?? null,
      storage_costs_unit: costs?.storage_costs_unit ?? '',
      mechanical_railcar_loading_costs: costs?.mechanical_railcar_loading_costs ?? null,
      rail_car_capacity: elevator.rail_car_capacity ?? null,
      expedite_costs: costs?.expedite_costs ?? null,
      documents_costs: costs?.documents_costs ?? null,
      other_costs: costs?.other_costs ?? null,
      composite_costs: costs?.composite_costs ?? null,
      is_composite_costs: costs?.is_composite_costs ?? false,
      comment: costs?.comment ?? '',
    },

    onSubmit: formHandler(
      (value) => {
        const {
          title,
          address,
          company,
          grain_elevator_railway_station,
          capacity,
          provided_services,
          rail_car_capacity,
          ...rest
        } = value
        const dto = {
          title,
          address,
          company,
          grain_elevator_railway_station,
          capacity,
          provided_services,
          rail_car_capacity,
          elevator_costs: rest,
        }
        return apiClient.put(endpoints.elevators(elevator.id), dto)
      },
      {
        onSuccess: () => {
          onSuccess()
          onClose()
        },

        onError: (err) => {
          const { errors } = err as RequestError
          formik.setErrors(removePropertyNamePrefix(errors, /elevator_costs./))
        },
      },
    ),
  })

  return (
    <ResponsiveModal
      id="editElevator"
      onClose={onClose}
      title={t('editElevator')}
      onConfirm={() => formik.submitForm()}
      progress={formProgress}
      disabled={!formik.dirty}
    >
      <ElevatorCommonFields formik={formik} />
    </ResponsiveModal>
  )
}
